import React, { useState } from "react";
import { Trash2, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../../lib/api";
import { fmtDateGB } from "../../lib/format";

export default function DocsTab({ projectId, docs, onSaved }) {
  const [form, setForm] = useState({ name: "", doc_type: "mcs", note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file && !form.name) { toast.error("Pick a file or enter a name"); return; }
    setBusy(true);
    try {
      let file_url = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post(`/uploads?project_id=${projectId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        file_url = `${API.replace('/api', '')}${up.data.download_url}`;
      }
      await api.post("/documents", {
        project_id: projectId,
        name: form.name || file?.name || "Document",
        doc_type: form.doc_type,
        file_url,
        note: form.note,
      });
      setForm({ name: "", doc_type: "mcs", note: "" });
      setFile(null);
      const el = document.getElementById("doc-file-input");
      if (el) el.value = "";
      toast.success("Document uploaded");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete document reference?")) return;
    await api.delete(`/documents/${id}`);
    onSaved();
  };
  const openFile = (url) => {
    if (!url) return;
    // Same-origin URLs ride the httpOnly auth cookie automatically. External
    // URLs (e.g. customer-uploaded https links) open as-is.
    window.open(url, "_blank");
  };
  return (
    <div className="space-y-6" data-testid="docs-tab">
      <section className="swiss-card-flat p-6">
        <div className="label mb-3">Upload document</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <input data-testid="doc-name" className="input-swiss" placeholder="Name (auto from filename)" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} />
          <select data-testid="doc-type" className="input-box" value={form.doc_type} onChange={(e)=>setForm({...form, doc_type: e.target.value})}>
            <option value="mcs">MCS</option>
            <option value="dno">DNO (G98/G99)</option>
            <option value="insurance">Insurance</option>
            <option value="warranty">Warranty</option>
            <option value="handover">Handover Pack</option>
            <option value="survey">Survey</option>
            <option value="other">Other</option>
          </select>
          <input id="doc-file-input" data-testid="doc-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="input-box" onChange={(e)=>setFile(e.target.files?.[0] || null)} />
          <input data-testid="doc-note" className="input-swiss" placeholder="Note (optional)" value={form.note} onChange={(e)=>setForm({...form, note: e.target.value})} />
          <button type="submit" disabled={busy} className="btn-primary justify-center" data-testid="doc-add-btn">
            <Upload size={13} /> {busy ? "Uploading…" : "Upload"}
          </button>
        </form>
        <div className="text-xs muted mt-2">PDF / PNG / JPG / WEBP · max 20MB · stored in Emergent Object Storage.</div>
      </section>
      <section className="swiss-card-flat overflow-hidden">
        <table className="data-table" data-testid="docs-table">
          <thead><tr><th>Name</th><th>Type</th><th>File</th><th>Added</th><th></th></tr></thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                <td className="font-medium"><FileText size={12} className="inline mr-1.5 muted" />{d.name}</td>
                <td><span className="tag">{d.doc_type}</span></td>
                <td>{d.file_url
                    ? <button onClick={() => openFile(d.file_url)} className="underline text-xs" data-testid={`doc-open-${d.id}`}>Open ↗</button>
                    : <span className="muted text-xs">—</span>}</td>
                <td className="text-xs muted">{fmtDateGB(d.uploaded_at)}</td>
                <td className="num"><button onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`}><Trash2 size={13} className="muted" /></button></td>
              </tr>
            ))}
            {!docs.length && <tr><td colSpan={5} className="muted text-center py-8">No documents.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ===== Monitoring =====
